import { Role, CancelationReason, SupplyCondition, CancelationUserType } from '@caresend/types';
import 'immutability-helper';
import 'lodash-unified';
import { isNullish } from './nullish.js';
import { objectEntries, objectKeys } from '../languageExtensions/object.js';
import { isNonEmptyString, isBoolean, isNumber, isObject, isEncrypted, isString } from './basicGuards.js';
import { isInEnum } from './enum.js';
import { getKeyValidationErrors, appendErrorMessage } from './validation.js';

/** @deprecated Use `getNonEmptyStringErrors` where possible. */

const getStringErrors = value => {
  if (isString(value)) return null;
  return `Expected string, found ${typeof value}`;
};
const getNonEmptyStringErrors = value => {
  if (isNonEmptyString(value)) return null;
  return `Expected non-empty string, found ${typeof value}`;
};
const getBooleanErrors = value => {
  if (isBoolean(value)) return null;
  return `Expected boolean, found ${typeof value}`;
};
const getNumberErrors = value => {
  if (isNumber(value)) return null;
  return `Expected number, found ${typeof value}`;
};
/**
 * - Valid: 1, 2, 3 ...
 * - Invalid: 0, -1, 1.5, ...
 */

const getPositiveIntegerErrors = value => {
  if (isNumber(value) && Number.isInteger(value) && value > 0) return null;
  return 'Expected positive integer, found ' + `${typeof value === 'number' ? value : typeof value}`;
};
const getNegativeNumberErrors = value => {
  if (isNumber(value) && value >= 0) return null;
  return `Expected non-negative number, found ${value}`;
};
const getEncryptedErrors = value => {
  if (isEncrypted(value)) return null;
  return `Expected encrypted data, found ${typeof value}`;
};
const getMaybeEncryptedErrors = validator => (value, parentObject) => {
  if (!isNullish(value) && !isEncrypted(value)) {
    return validator(value, parentObject);
  }

  return null;
};
const elementTimestampValidationKeys = {
  id: getNonEmptyStringErrors,
  timestamp: getNumberErrors
};
const getElementTimestampErrors = value => {
  if (isObject(value)) {
    return getKeyValidationErrors(value, elementTimestampValidationKeys);
  }

  return `Expected object, found ${typeof value}`;
};
const timeZoneDateValidationKeys = {
  timestamp: getNumberErrors,
  timeZone: getNonEmptyStringErrors
};
const getTimeZoneDateErrors = value => {
  if (isObject(value)) {
    return getKeyValidationErrors(value, timeZoneDateValidationKeys);
  }

  return `Expected object, found ${typeof value}`;
};
/**
 * Can be used to wrap a validator for optional data. Currently
 * requires type and key as type parameters.
 *
 * Type params: `<Type expected, Parent object type>`
 */

const getOptionalErrors = validator => (value, parentObject) => {
  if (!isNullish(value)) return validator(value, parentObject);
  return null;
};
/**
 * Can be used to wrap a validator for array data.
 *
 * Type params: `<Type expected (per array element), Parent object type>`
 */

const getArrayErrors = (itemValidator, minLength = 0) => (value, parentObject) => {
  if (Array.isArray(value)) {
    if (value.length < minLength) return `Expected at least ${minLength} items`;
    const errors = value.reduce((errorMessagesOrNull, item, index) => {
      const error = itemValidator(item, parentObject);
      const newMessage = error && `Item at index ${index} failed validation: ${error}`;
      if (newMessage) return appendErrorMessage(errorMessagesOrNull, newMessage);
      return errorMessagesOrNull;
    }, null);
    return errors;
  }

  return 'Expected array.';
};
/**
 * Can be used to wrap a validator for `ByID<...>` data.
 *
 * Type params: `<Type expected (value per object key), Parent object type>`
 */

const getByIdErrors = (itemValidator, minLength = 0) => (value, parentObject) => {
  if (isObject(value)) {
    const entries = objectEntries(value);
    if (entries.length < minLength) return `Expected at least ${minLength} items`;
    const errors = entries.reduce((errorMessagesOrNull, [key, item]) => {
      const error = itemValidator(item, parentObject);
      const newMessage = error && `Item at key ${key} failed validation: ${error}`;
      if (newMessage) return appendErrorMessage(errorMessagesOrNull, newMessage);
      return errorMessagesOrNull;
    }, null);
    return errors;
  }

  return 'Expected ByID object';
};
/**
 * Validate strict keys - this does not wrap a validator, but can be used
 * separately to validate an object where we want to avoid extra keys that
 * aren't in a defined list. (For example, in `ProcessingInstructionSet`,
 * we do not want to accept `{ centrifyooge: true }`)
 *
 * Pass an object which has as its keys all of the allowable keys (value does
 * not matter), and an object to validate.
 */

const getStrictKeysErrors = (objectWithAllowedKeys, value) => {
  if (isObject(value)) {
    const keys = objectKeys(value);
    const errors = keys.reduce((errorMessagesOrNull, key) => {
      const keyMatches = !!objectWithAllowedKeys[key];

      if (!keyMatches) {
        const message = `Unexpected key ${String(key)}`;
        return appendErrorMessage(errorMessagesOrNull, message);
      }

      return errorMessagesOrNull;
    }, null);
    return errors;
  }

  return 'Expected object.';
};
const buildStringEnumErrorsGetter = enumerator => value => {
  if (!value) return 'Expected string matching enum, got undefined.';
  if (isInEnum(value, enumerator)) return null;
  return `Expected string matching enum, got ${value}`;
};
const buildSpecificStringErrorGetter = specificString => value => {
  if (value === specificString) return null;
  return `Expected string matching ${specificString}, got ${value}`;
};
const buildStatusEnumErrorGetter = enumerator => value => {
  if (!value) return 'Expected Status object, got undefined.';
  if (!value.status) return 'Expected status property in Status object, got undefined.';
  return buildStringEnumErrorsGetter(enumerator)(value.status);
};
const objectCanceledValidationKeys = {
  canceledBy: getNonEmptyStringErrors,
  canceledByRole: buildStringEnumErrorsGetter(Role),
  timestamp: getNumberErrors,
  reason: getOptionalErrors(buildStringEnumErrorsGetter(CancelationReason)),
  // TODO: Use `getNonEmptyStringErrors` if CARE-5265 is resolved
  detailedReason: getOptionalErrors(getStringErrors),
  supplyCondition: getOptionalErrors(buildStringEnumErrorsGetter(SupplyCondition)),
  userType: getOptionalErrors(buildStringEnumErrorsGetter(CancelationUserType))
};

export { buildSpecificStringErrorGetter, buildStatusEnumErrorGetter, buildStringEnumErrorsGetter, getArrayErrors, getBooleanErrors, getByIdErrors, getElementTimestampErrors, getEncryptedErrors, getMaybeEncryptedErrors, getNegativeNumberErrors, getNonEmptyStringErrors, getNumberErrors, getOptionalErrors, getPositiveIntegerErrors, getStrictKeysErrors, getStringErrors, getTimeZoneDateErrors, objectCanceledValidationKeys };
